// src/index.ts
import { createTRPCProxyClient, httpLink } from "@trpc/client";

// src/retry_link.ts
import { retryLink, TRPCClientError } from "@trpc/client";
var QUERY_MAX_ATTEMPTS = 4;
var MUTATION_MAX_ATTEMPTS = 2;
var BASE_DELAY_MS = 400;
var MAX_DELAY_MS = 2e4;
function readHttpStatus(error) {
  const data = error.data;
  if (data && typeof data.httpStatus === "number") return data.httpStatus;
  const meta = error.meta;
  const s = meta?.response?.status;
  return typeof s === "number" ? s : void 0;
}
function isTransientTrpcOrNetworkError(error) {
  const msg = (error.message ?? "").toLowerCase();
  if (msg.includes("aborted")) return false;
  if (error instanceof TRPCClientError) {
    const httpStatus = readHttpStatus(error);
    if (httpStatus === 401 || httpStatus === 403) return false;
    const code = error.data?.code;
    if (code === "UNAUTHORIZED" || code === "FORBIDDEN" || code === "BAD_REQUEST" || code === "PARSE_ERROR") return false;
    if (httpStatus === 429 || httpStatus === 502 || httpStatus === 503 || httpStatus === 504) return true;
    if (httpStatus != null && httpStatus >= 500) return true;
    if (error.data == null && (msg.includes("fetch failed") || msg.includes("failed to fetch"))) return true;
  }
  if (msg.includes("fetch failed") || msg.includes("failed to fetch") || msg.includes("network error")) return true;
  const cause = error.cause;
  if (cause instanceof Error) return isTransientTrpcOrNetworkError(cause);
  if (cause && typeof cause === "object") {
    const code = cause.code;
    if (typeof code === "string") {
      if (code === "ETIMEDOUT" || code === "ECONNRESET" || code === "ENOTFOUND" || code === "EAI_AGAIN") return true;
      if (code === "UND_ERR_CONNECT_TIMEOUT" || code === "UND_ERR_SOCKET") return true;
    }
  }
  if (typeof AggregateError !== "undefined" && error instanceof AggregateError) return true;
  return false;
}
function createOwlbertRetryLink() {
  return retryLink({
    retry(opts) {
      if (!isTransientTrpcOrNetworkError(opts.error)) return false;
      const max = opts.op.type === "mutation" ? MUTATION_MAX_ATTEMPTS : QUERY_MAX_ATTEMPTS;
      return opts.attempts < max;
    },
    retryDelayMs: (attemptIndex) => Math.min(BASE_DELAY_MS * 2 ** attemptIndex, MAX_DELAY_MS)
  });
}

// src/safe_storage.ts
function getStorage() {
  try {
    if (typeof window === "undefined") return null;
    const storage = window.localStorage;
    if (!storage) return null;
    return storage;
  } catch {
    return null;
  }
}
function isLocalStorageAvailable() {
  const storage = getStorage();
  if (!storage) return false;
  try {
    const probe = "__owlbert_storage_probe__";
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}
function safeStorageGet(key) {
  const storage = getStorage();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}
function safeStorageSet(key, value) {
  const storage = getStorage();
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}
function safeStorageRemove(key) {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
  }
}

// src/token_clock.ts
var REFRESH_BEFORE_EXPIRY_SEC = 300;
var SKEW_STORAGE_KEY = "owlbert_clock_skew_v1";
var SKEW_NOISE_FLOOR_SEC = 60;
var MAX_TRUSTED_SKEW_SEC = 365 * 24 * 60 * 60;
var cachedSkewSec = null;
function decodeBase64Url(segment) {
  const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
  if (typeof Buffer !== "undefined") {
    return Buffer.from(segment, "base64url").toString("utf-8");
  }
  return decodeURIComponent(escape(atob(normalized)));
}
function parseJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3 || !parts[1]) return null;
    const payload = JSON.parse(decodeBase64Url(parts[1]));
    return payload && typeof payload === "object" ? payload : null;
  } catch {
    return null;
  }
}
function localNowSec() {
  return Math.floor(Date.now() / 1e3);
}
function getClockSkewSec() {
  if (cachedSkewSec !== null) return cachedSkewSec;
  const raw = safeStorageGet(SKEW_STORAGE_KEY);
  const parsed = raw === null ? Number.NaN : Number(raw);
  cachedSkewSec = Number.isFinite(parsed) && Math.abs(parsed) <= MAX_TRUSTED_SKEW_SEC ? parsed : 0;
  return cachedSkewSec;
}
function correctedNowSec() {
  return localNowSec() - getClockSkewSec();
}
function noteFreshAccessToken(token) {
  if (!token) return;
  const payload = parseJwtPayload(token);
  if (!payload || typeof payload.iat !== "number") return;
  const rawSkew = localNowSec() - payload.iat;
  if (Math.abs(rawSkew) > MAX_TRUSTED_SKEW_SEC) return;
  const skew = Math.abs(rawSkew) < SKEW_NOISE_FLOOR_SEC ? 0 : rawSkew;
  cachedSkewSec = skew;
  safeStorageSet(SKEW_STORAGE_KEY, String(skew));
}
function isAccessTokenExpiredOrSoon(token, bufferSec = REFRESH_BEFORE_EXPIRY_SEC) {
  const payload = parseJwtPayload(token);
  if (!payload) return true;
  if (typeof payload.exp !== "number") return false;
  const lifetimeSec = typeof payload.iat === "number" && payload.exp > payload.iat ? payload.exp - payload.iat : null;
  const buffer = lifetimeSec === null ? bufferSec : Math.min(bufferSec, Math.floor(lifetimeSec / 2));
  return payload.exp <= correctedNowSec() + buffer;
}

// src/auth.ts
var InMemorySessionStore = class {
  access_token = null;
  refresh_token = null;
  getSession() {
    if (!this.access_token) return null;
    return {
      access_token: this.access_token,
      refresh_token: this.refresh_token ?? void 0
    };
  }
  setSession(access_token, refresh_token) {
    this.access_token = access_token;
    this.refresh_token = refresh_token ?? null;
  }
  clearSession() {
    this.access_token = null;
    this.refresh_token = null;
  }
};
var DefaultTokenStorage = class {
  memoryToken = null;
  storageKey = "owlbert_access_token";
  getToken() {
    return safeStorageGet(this.storageKey) ?? this.memoryToken;
  }
  setToken(token) {
    if (token) {
      safeStorageSet(this.storageKey, token);
    } else {
      safeStorageRemove(this.storageKey);
    }
    this.memoryToken = token;
  }
  clearToken() {
    this.setToken(null);
  }
};
function isSessionStore(s) {
  return !!s && typeof s.getSession === "function" && typeof s.setSession === "function";
}
function createAuthHelper(client, storage) {
  const tokenStorage = storage || new DefaultTokenStorage();
  const useSessionStore = isSessionStore(tokenStorage);
  return {
    /**
     * Sign up a new user
     * The access token (and refresh_token when using SessionStore) will be stored if a session is returned
     */
    async signUp(email, password) {
      const result = await client.auth.signup.mutate({ email, password });
      const session = result.session;
      if (session?.access_token) {
        noteFreshAccessToken(session.access_token);
        if (useSessionStore) {
          tokenStorage.setSession(session.access_token, session.refresh_token ?? null);
        } else {
          tokenStorage.setToken(session.access_token);
        }
      }
      return result;
    },
    /**
     * Sign in with email and password
     * The access token (and refresh_token when using SessionStore) will be stored
     */
    async signIn(email, password) {
      const result = await client.auth.login.mutate({ email, password });
      const session = result.session;
      if (session?.access_token) {
        noteFreshAccessToken(session.access_token);
        if (useSessionStore) {
          tokenStorage.setSession(session.access_token, session.refresh_token ?? null);
        } else {
          tokenStorage.setToken(session.access_token);
        }
      }
      return result;
    },
    /**
     * Sign out the current user
     * The stored token(s) will be cleared
     */
    async signOut() {
      try {
        await client.auth.logout.mutate();
      } finally {
        if (useSessionStore) tokenStorage.clearSession();
        else tokenStorage.clearToken();
      }
    },
    /**
     * Get the current authenticated user
     * Requires a valid token to be stored
     */
    async getCurrentUser() {
      return await client.auth.me.query();
    },
    /**
     * Get the stored access token
     */
    getToken() {
      if (useSessionStore) return tokenStorage.getSession()?.access_token ?? null;
      return tokenStorage.getToken();
    },
    /**
     * Set the access token manually (SessionStore: use setSession for both tokens)
     */
    setToken(token) {
      if (useSessionStore) {
        if (token) tokenStorage.setSession(token, tokenStorage.getSession()?.refresh_token ?? null);
        else tokenStorage.clearSession();
      } else {
        tokenStorage.setToken(token);
      }
    },
    /**
     * Clear the stored token(s)
     */
    clearToken() {
      if (useSessionStore) tokenStorage.clearSession();
      else tokenStorage.clearToken();
    },
    /**
     * Check if a user is currently authenticated (has a token)
     */
    isAuthenticated() {
      return this.getToken() !== null;
    }
  };
}

// ../../src/backend/util/zod.ts
import * as zodPkg from "zod";
var z2 = zodPkg.z;

// ../../src/backend/schemas/llm-tools.ts
var toolInputSchemaSchema = z2.object({
  type: z2.literal("object"),
  /** Zod 4 requires explicit key schema: `z.record(z.string(), z.unknown())` (single-arg `z.record(z.unknown())` breaks at parse). */
  properties: z2.record(z2.string(), z2.unknown()).default({}),
  required: z2.array(z2.string()).optional()
});
var llmToolSchema = z2.object({
  id: z2.string(),
  description: z2.string().optional(),
  purpose: z2.string().optional(),
  input_schema: toolInputSchemaSchema.optional(),
  parameters: toolInputSchemaSchema.optional()
});
function normalizeToolInputSchema(tool) {
  if (tool.input_schema && typeof tool.input_schema === "object" && "type" in tool.input_schema && "properties" in tool.input_schema) {
    const parsed = toolInputSchemaSchema.safeParse(tool.input_schema);
    if (parsed.success) return parsed.data;
  }
  if (tool.parameters && typeof tool.parameters === "object") {
    const parsed = toolInputSchemaSchema.safeParse(tool.parameters);
    if (parsed.success) return parsed.data;
  }
  return { type: "object", properties: {} };
}

// src/index.ts
function isInvalidRefreshTokenError(err) {
  const msg = err?.message ?? String(err);
  return /refresh token not found|invalid refresh token|refresh_token.*invalid|revoked/i.test(msg);
}
var inFlightByUrl = /* @__PURE__ */ new Map();
async function ensureValidToken(sessionStore, url, opts) {
  const session = sessionStore.getSession();
  if (!session?.access_token && !session?.refresh_token) return null;
  const hasValidAccess = !!(session.access_token && !isAccessTokenExpiredOrSoon(session.access_token));
  if (!opts?.force && hasValidAccess) return session.access_token ?? null;
  if (!session.refresh_token) return session.access_token ?? null;
  const existing = inFlightByUrl.get(url);
  if (existing) {
    await existing;
    return sessionStore.getSession()?.access_token ?? null;
  }
  const doRefresh = async () => {
    const refreshClient = createOwlbertClient({ url });
    const currentSession = sessionStore.getSession();
    if (!currentSession?.refresh_token) return null;
    try {
      const result = await refreshClient.auth.refresh.mutate({ refresh_token: currentSession.refresh_token });
      const newSession = result.session;
      if (newSession?.access_token) {
        noteFreshAccessToken(newSession.access_token);
        sessionStore.setSession(newSession.access_token, newSession.refresh_token ?? currentSession.refresh_token);
        return newSession.access_token;
      }
    } catch (err) {
      if (isInvalidRefreshTokenError(err)) {
        sessionStore.clearSession();
      }
      return null;
    } finally {
      inFlightByUrl.delete(url);
    }
    return null;
  };
  const promise = doRefresh();
  inFlightByUrl.set(url, promise);
  return promise;
}
function createOwlbertClient(options = {}) {
  const {
    url = "/trpc",
    token,
    headers: additionalHeaders,
    trpcOptions
  } = options;
  const getToken = () => {
    if (!token) return null;
    if (typeof token === "string") return token;
    if (typeof token === "function") return token();
    return token.getToken();
  };
  const getHeaders = async () => {
    const headers = {};
    const tokenValue = await getToken();
    if (tokenValue) {
      headers.authorization = `Bearer ${tokenValue}`;
    }
    if (additionalHeaders) {
      const extraHeaders = typeof additionalHeaders === "function" ? await additionalHeaders() : additionalHeaders;
      Object.assign(headers, extraHeaders);
    }
    return headers;
  };
  return createTRPCProxyClient({
    ...trpcOptions,
    links: [
      createOwlbertRetryLink(),
      httpLink({
        url,
        async headers() {
          return await getHeaders();
        }
      })
    ]
  });
}
function createOwlbertClientWithAuth(options = {}) {
  const { tokenStorage, ...clientOptions } = options;
  const storage = tokenStorage || new DefaultTokenStorage();
  const client = createOwlbertClient({
    ...clientOptions,
    token: () => storage.getToken()
  });
  const auth = createAuthHelper(client, storage);
  return { client, auth };
}
function createOwlbertClientWithSession(options) {
  const { url = "/trpc", sessionStore, headers: additionalHeaders, trpcOptions } = options;
  const getHeaders = async () => {
    const token = await ensureValidToken(sessionStore, url);
    const headers = {};
    if (token) headers.authorization = `Bearer ${token}`;
    if (additionalHeaders) {
      const extra = typeof additionalHeaders === "function" ? await additionalHeaders() : additionalHeaders;
      Object.assign(headers, extra);
    }
    return headers;
  };
  const nativeFetch = globalThis.fetch.bind(globalThis);
  async function sessionAwareFetch(input, init) {
    const res = await nativeFetch(input, init);
    if (res.status !== 401) return res;
    const alreadyRetried = Boolean(init?.__owlbert401Retried);
    if (alreadyRetried) return res;
    const sess = sessionStore.getSession();
    if (!sess?.refresh_token) return res;
    await ensureValidToken(sessionStore, url, { force: true });
    const merged = await getHeaders();
    const nextHeaders = new Headers(init?.headers ?? void 0);
    for (const [k, v] of Object.entries(merged)) {
      nextHeaders.set(k, v);
    }
    return nativeFetch(input, { ...init, headers: nextHeaders, __owlbert401Retried: true });
  }
  const client = createTRPCProxyClient({
    ...trpcOptions,
    links: [
      createOwlbertRetryLink(),
      httpLink({
        url,
        async headers() {
          return await getHeaders();
        },
        fetch: sessionAwareFetch
      })
    ]
  });
  const auth = createAuthHelper(client, sessionStore);
  return { client, auth };
}
var index_default = createOwlbertClient;
export {
  InMemorySessionStore,
  REFRESH_BEFORE_EXPIRY_SEC,
  correctedNowSec,
  createAuthHelper,
  createOwlbertClient,
  createOwlbertClientWithAuth,
  createOwlbertClientWithSession,
  createOwlbertRetryLink,
  index_default as default,
  getClockSkewSec,
  isAccessTokenExpiredOrSoon,
  isLocalStorageAvailable,
  isTransientTrpcOrNetworkError,
  llmToolSchema,
  normalizeToolInputSchema,
  noteFreshAccessToken,
  safeStorageGet,
  safeStorageRemove,
  safeStorageSet,
  toolInputSchemaSchema
};
//# sourceMappingURL=index.js.map